/**
 * @description: RouteRecordRaw 参数说明
 * @param: path {string} 跳转路径
 * @param: redirect {string} 重定向跳转路径
 * @param: component {string} 组件
 * @param: meta {object} 可以自定义一些内容
 *         visible {boolean} 一级菜单只有一个二级菜单时是否始终显示。（因为有的一级菜单只有一个二级菜单）
 *         hidden {Boolean} 默认false， 为true表示此路由不作为菜单展示
 *         hideAside {Boolean} 默认false， 为true表示隐藏左侧导航
 *         icon {string} 菜单对应的图标icon, 推荐使用svg形式
 *         title {string} 菜单对应的标题，也作为浏览器标签标题展示
 *         authCodes {array} 用户权限code
 *         operations {array} 当前页面组件用户有权限的操作的 code 列表
 *         isBlank {boolean} 默认false， 是否需要在浏览器新标签页中打开
 *         activeMenu {string} 激活菜单的index, index取的是路由中path的值, 解决有不在菜单中展示的页面时菜单激活指定菜单
 *
 *         setBreadcrumbs {function} 重置面包屑内容
 *
 *         module {string} 如果两个路由使用同一个组件，一定要设置上module。如：module: @/views/Table/TableList3/EditPage
 *         noCache {boolean} 默认false，页面是否被缓存，在使用多导航标签时有效，true为不缓存
 * @return void {*}
 */
import {
  createRouter, createWebHistory, RouteRecordRaw, RouteLocationNormalized,
} from 'vue-router';
import { isEmpty } from '@/utils/index';
import config from '@/config';

import { useUserStore } from '@/store/modules/user';
import { usePermissionStore } from '@/store/modules/permission';

import { errorRoutes } from './modules/error';
import { unifiedAnalysisRoutes } from './modules/unifiedAnalysis';
import { demandCenterRoutes } from './modules/demandCenter';
import { downloadCenterRoutes } from './modules/downloadCenter';
import { selfAnalysisRoutes } from './modules/selfAnalysis';
import { selfAnalysisConfigRoutes } from './modules/selfAnalysisConfig';

let userStore: TypeObject;
let permissionStore: TypeObject;

export const constantRoutes: Array<RouteRecordRaw> = [];
if (!config.sso) {
  constantRoutes.unshift({
    path: '/',
    redirect: '/error/401',
    meta: {
      hidden: true,
    },
  });
}

export const catchAllRoutes: Array<RouteRecordRaw> = [
  // {
  //   path: '/redirect',
  //   component: Layout,
  //   name: 'redirect',
  //   meta: {
  //     hidden: true,
  //   },
  //   children: [
  //     {
  //       path: '/redirect/:catchAll(.*)',
  //       component: Redirect,
  //     }
  //   ],
  // },
  {
    path: '/error',
    name: 'error',
    // component: Layout,
    redirect: '/error/404',
    meta: {
      hidden: true,
    },
    children: [
      {
        path: '/error/404',
        name: '404',
        component: () => import('@/views/404.vue'),
        meta: {
          hidden: true,
        },
      },
      {
        path: '/error/401',
        name: '401',
        component: () => import('@/views/401.vue'),
        meta: {
          hidden: true,
          hideAside: true,
        },
      }
    ],
  },
  {
    path: '/report/share',
    name: 'reportShare',
    component: () => import('@/views/report.vue'),
    meta: {
      hidden: true,
    },
  },
  //  首页隐藏：登录首页的默认转到统一分析
  {
    path: '/home/index',
    name: 'none',
    redirect: '/unified-analysis/index',
    meta: {
      hidden: true,
    },
  },
];
if (!config.sso) {
  catchAllRoutes.push({
    path: '/:catchAll(.*)',
    redirect: '/error/404',
    name: 'errorCatch',
    meta: {
      hidden: true,
    },
  });
}

export const asyncRoutes: Array<RouteRecordRaw> = [
  //  首页隐藏：登录首页的默认转到统一分析
  // ...homeRoutes,
  ...unifiedAnalysisRoutes,
  ...selfAnalysisRoutes,
  // ...selfAnalysisDetailRoutes,
  ...demandCenterRoutes,
  // ...demandDetailRoutes,
  ...downloadCenterRoutes,
  ...selfAnalysisConfigRoutes,
  ...errorRoutes
];

const router = createRouter({
  history: createWebHistory(),
  routes: config.sso
    ? [...constantRoutes, ...catchAllRoutes]
    : [...constantRoutes, ...asyncRoutes, ...catchAllRoutes],
});

router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: any) => {
  if (!userStore) {
    userStore = useUserStore();
  }
  if (!permissionStore) {
    permissionStore = usePermissionStore();
  }

  // 需要根据条件动态设置面包屑的时候可以在meta中设置setBreadcrumbs函数
  if (typeof to.meta.setBreadcrumbs === 'function') {
    to.meta.breadcrumbs = to.meta.setBreadcrumbs(to);
  }
  // 使用同一个页面组件的不同路由
  const toModule = to.meta.module;
  const fromModule = from.meta.module;
  const isComponentReuse = !isEmpty(toModule) && toModule === fromModule;

  if (config.sso) { // 使用集团通用的登录方式
    const hasMenus = userStore.menus && userStore.menus.length > 0;
    if (hasMenus) {
      if (isComponentReuse) {
        next(`/redirect${to.fullPath}`);
      } else {
        next();
      }
    } else {
      const { menuKeys } = await userStore.getUserInfo();
      const { accessedRoutes, permissionRoutes } = await permissionStore.generateRoutes(menuKeys);
      console.log('根据用户权限动态生成的路由:', accessedRoutes);

      //  设置用户输入 / 跳转至有权限的第一个路由
      const firstRoute = permissionRoutes.filter((item: RouteRecordRaw) => !config.excludePath.includes(item.path) && item.meta)[0];
      if (firstRoute) {
        accessedRoutes.unshift({
          path: '/',
          redirect: firstRoute.path,
          meta: {
            hidden: true,
            title: '一站式分析平台',
          },
        });
      }

      // 动态添加路由
      accessedRoutes.forEach((item: any) => {
        router.addRoute(item);
      });

      // 配置404
      router.addRoute({
        path: '/:catchAll(.*)',
        redirect: '/404',
        meta: {
          hidden: true,
        },
      });

      // 用户没有菜单权限 进入401 用户无权限页面
      if (!menuKeys.length) {
        if (config.excludePath.includes(to.path)) {
          next();
          return;
        }
        next('/error/401');
        return;
      }

      next({ ...to, replace: true });
    }
  } else if (isComponentReuse) {
    next(`/redirect${to.fullPath}`);
  } else {
    next();
  }
});

router.afterEach((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  window.zhuge.track('进入页面', {
    所在页面: to.meta.title,
    上级页面: from.meta.title,
  });

  setTimeout(() => {
    window.document.title = to.meta.title ? `一站式分析平台 - ${to.meta.title}` : '一站式分析平台';
  }, 0);
});

// 路由切换接入apm监控
if ((window as any).MAIAAPM && (window as any).MAIAAPM.wrapVueRouter) {
  (window as any).MAIAAPM.wrapVueRouter(router, {
    endDelay: 500, // 切换路由后延迟结束时间
  });
}

export default router;
